if (!customElements.get('sibling-product-picker')) {
  customElements.define(
    'sibling-product-picker',
    class SiblingProductPicker extends HTMLElement {
      connectedCallback() {
        this.onClick = this.onClick?.bind(this) || this.handleClick.bind(this);
        this.addEventListener('click', this.onClick);
      }

      disconnectedCallback() {
        if (this.onClick) this.removeEventListener('click', this.onClick);
      }

      getSectionId() {
        const productInfo = this.closest('product-info');
        return productInfo?.dataset?.section;
      }

      getCurrentSelectedOptions() {
        const productInfo = this.closest('product-info');
        const variantRadios = productInfo?.querySelector('variant-radios');
        const variantSelects = productInfo?.querySelector('variant-selects');

        const getOptionsFromFieldsets = (root) => {
          const fieldsets = Array.from(root.querySelectorAll('fieldset'));
          return fieldsets.map((fieldset) => {
            const checked = fieldset.querySelector('input[type="radio"]:checked');
            return checked ? checked.value : null;
          });
        };

        const getOptionsFromSelects = (root) => {
          const selects = Array.from(root.querySelectorAll('select'));
          return selects.map((select) => select.value || null);
        };

        if (variantRadios) return getOptionsFromFieldsets(variantRadios).filter(Boolean);
        if (variantSelects) return getOptionsFromSelects(variantSelects).filter(Boolean);
        return [];
      }

      async resolveSiblingVariantId(productHandleOrUrl) {
        const matchMode = this.dataset.matchMode || 'first_available';
        const productUrl = productHandleOrUrl.startsWith('/')
          ? productHandleOrUrl
          : `/products/${productHandleOrUrl}`;

        const res = await fetch(`${productUrl}.js`, { credentials: 'same-origin' });
        if (!res.ok) throw new Error(`Failed to fetch product JSON for ${productUrl}`);
        const data = await res.json();

        if (!data?.variants?.length) return null;

        if (matchMode === 'match_options') {
          const selectedOptions = this.getCurrentSelectedOptions();
          if (selectedOptions.length) {
            const matched = data.variants.find((v) => {
              const vOpts = [v.option1, v.option2, v.option3].filter(Boolean);
              if (vOpts.length < selectedOptions.length) return false;
              return selectedOptions.every((opt, idx) => vOpts[idx] === opt);
            });
            if (matched) return matched.id;
          }
        }

        const firstAvailable = data.variants.find((v) => v.available);
        return (firstAvailable || data.variants[0]).id;
      }

      async swapMainProductSection(productUrl, variantId) {
        const sectionId = this.getSectionId();
        if (!sectionId) throw new Error('Missing section id');

        const currentSection = document.getElementById(`MainProduct-${sectionId}`);
        if (!currentSection) throw new Error('Could not find current MainProduct section');

        const res = await fetch(`${productUrl}?variant=${variantId}&section_id=${sectionId}`, {
          credentials: 'same-origin'
        });
        if (!res.ok) throw new Error(`Failed to fetch section HTML for ${productUrl}`);

        const htmlText = await res.text();
        const html = new DOMParser().parseFromString(htmlText, 'text/html');
        const updatedSection = html.getElementById(`MainProduct-${sectionId}`);
        if (!updatedSection) throw new Error('Fetched HTML did not include expected MainProduct section');

        currentSection.replaceWith(updatedSection);

        document.dispatchEvent(
          new CustomEvent('main-product-section:replaced', {
            bubbles: true,
            detail: { sectionId }
          })
        );

        const nextUrl = `${productUrl}?variant=${variantId}`;
        window.history.pushState({ variantId, productUrl }, '', nextUrl);
      }

      async handleClick(event) {
        const btn = event.target.closest('[data-sibling-product-url]');
        if (!btn || !this.contains(btn)) return;
        event.preventDefault();

        const productUrl = btn.dataset.siblingProductUrl;
        if (!productUrl) return;

        try {
          this.classList.add('is-loading');
          const variantId = await this.resolveSiblingVariantId(productUrl);
          await this.swapMainProductSection(productUrl, variantId);
        } catch (e) {
          console.error(e);
        } finally {
          this.classList.remove('is-loading');
        }
      }
    }
  );
}

